/**
 * One shop card (TRD-09 §6) — "the most important component".
 *
 * Towers are identical 16×16 shapes on the battlefield, so this card is where a tower's
 * identity actually lives: what it is, what it costs, and — above everything else — what
 * buying it does to the synergy web. The preview line is the feature that teaches the
 * game (R-09-07), which is why it gets the largest visual weight after the name.
 *
 * Purely presentational. Every decision (disable reason, preview text, green highlight)
 * is made in `shopCardModel.ts`; every number on the card was computed by the sim.
 */
import { CLASS_COLORS, getClass, getTower } from '@prism/content';
import type { ClassId } from '@prism/sim';
import {
  disabledReason,
  formatInterval,
  formatRange,
  ownedBadge,
  previewLines,
  type ShopSlotSnapshot,
} from './shopCardModel.js';

interface ShopCardProps {
  slot: ShopSlotSnapshot | null;
  index: number;
  gold: number;
  benchFull: boolean;
  buildPhase: boolean;
  onBuy: (slot: number) => void;
}

/** Tier → border colour. Tier is a rarity read, and colour carries it faster than a number. */
const TIER_BORDER: Record<1 | 2 | 3, string> = {
  1: '#3a3f4a',
  2: '#4a6fa5',
  3: '#a06fd0',
};

export function ShopCard({ slot, index, gold, benchFull, buildPhase, onBuy }: ShopCardProps) {
  if (!slot) {
    return <div className="shop-card shop-card--empty">—</div>;
  }

  const def = getTower(slot.defId);
  const reason = disabledReason(slot, gold, benchFull);
  const lines = previewLines(slot.deltas);
  const owned = ownedBadge(slot);
  const affordable = reason === null;

  return (
    <button
      type="button"
      className={`shop-card${affordable ? '' : ' shop-card--disabled'}`}
      style={{ borderColor: TIER_BORDER[def.tier] }}
      // Dimmed, not hidden, with the reason where the cursor already is (R-09-08). The
      // card stays readable while disabled — an unaffordable card is still information.
      disabled={!affordable}
      title={reason ?? `Buy ${def.name} (${index + 1})`}
      onClick={() => onBuy(index)}
    >
      <span className="shop-card__head">
        <span className="shop-card__name">{def.name}</span>
        <span className="shop-card__cost">{slot.cost}g</span>
      </span>

      <span className="shop-card__classes">
        {def.classes.map((classId) => (
          <ClassChip key={classId} classId={classId as ClassId} />
        ))}
        {slot.wouldMerge && buildPhase && (
          // The strongest purchase motivation in the game (R-09-09) — the player must
          // never count copies by hand. Buying auto-merges (TRD-07 R-07-09), so the
          // badge can promise the star outright.
          <span className="shop-card__merge">★ MERGE → ★2</span>
        )}
        {owned && !slot.wouldMerge && (
          // Same principle one step earlier: the merge badge only appears on the copy
          // that completes it, which left the first two copies invisible unless the
          // player remembered them. This is the part of "never count copies by hand"
          // that was missing.
          <span className="shop-card__owned">{owned}</span>
        )}
      </span>

      {def.support ? (
        // B-15 — a support tower has no combat stats; printing "0 dmg" would read as
        // a broken card rather than a different archetype.
        <span className="shop-card__stats muted">
          SUPPORT · never attacks · {formatRange(def.base.range)} aura
        </span>
      ) : (
        <span className="shop-card__stats muted">
          {def.base.damage} dmg{def.base.projectile.kind === 'zone' ? '/strike' : ''} ·{' '}
          {formatInterval(def.base.attackInterval)} · {formatRange(def.base.range)} rng
        </span>
      )}

      {/* Archetype towers (B-15/17/18/19) carry identity the stat line cannot: what the
          aura does, what the brood is, how the zone behaves. One authored sentence. */}
      {(def.support || def.innateEffects.length > 0 || def.base.projectile.kind === 'zone') && (
        <span className="shop-card__desc muted">{def.description}</span>
      )}

      {lines.map((line) => (
        <span
          key={line.classId}
          className={`shop-card__preview${line.crossesThreshold ? ' shop-card__preview--unlock' : ''}`}
        >
          {line.text}
          {line.unlocked && <span className="shop-card__unlock-text"> {line.unlocked}</span>}
        </span>
      ))}
    </button>
  );
}

/**
 * Per-class SHAPE badges for colorblind mode (R-09-27). Ten colour-coded classes on
 * identical silhouettes is a real accessibility problem and colour-shifting alone does
 * not solve it — a distinct shape per class does. Rendered always, shown by CSS only
 * when the root carries `.colorblind`, so every ClassChip call site inherits the
 * behaviour without threading the setting through props.
 */
const CLASS_SHAPES: Record<ClassId, string> = {
  warden: '■',
  marksman: '➤',
  arcanist: '✦',
  venom: '☰',
  frost: '▲',
  tempest: '↯',
  alchemist: '◆',
  beacon: '●',
  swarm: '⁘',
  void: '◐',
};

/** Class chip: colour swatch + glyph + name (R-09-06). Reused by the synergy panel later. */
export function ClassChip({ classId }: { classId: ClassId }) {
  const def = getClass(classId);
  const colors = CLASS_COLORS[classId];
  return (
    <span className="class-chip" style={{ color: colors.color, borderColor: colors.color }}>
      <i className="chip-shape" aria-hidden>
        {CLASS_SHAPES[classId]}
      </i>
      <b>{def.glyph}</b> {def.name}
    </span>
  );
}
